import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import CollapsibleGroup from 'components/Common/CollapsibleGroup/CollapsibleGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import AgentHubMarkerSlider from './AgentHubMarkerSlider'
import Label from 'LuxKit/Label/Label'
import Tooltip from 'LuxKit/Tooltip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { setAgentExtraEarnings } from 'actions/AgentHubActions'
import { trackClientEvent } from 'analytics/analytics'
import useDebouncedCallback from 'hooks/useDebouncedCallback'

const ANALYTICS_CATEGORY = 'agent_hub'
const ANALYTICS_DEBOUNCE_MS = 600

const MAX_MARKUP_PERCENTAGE = 20
const checkIcon = <LineCheckIcon />

const FullWidthSheet = styled(Sheet)`
  width: 100%;
`

const ContentSpacer = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
`

interface Props {
  offerId: string
  offerTotal: number
  /** The "valued up to" retail price, used to calculate customer savings */
  retailValue?: number
  packageId?: string
  optionId?: string
  offer?: App.AgentHubOfferPayload
  /** When provided, bypasses the commission hook entirely (used in checkout where commission comes from the cart endpoint) */
  commissionPercentage?: number
  /** LuxPlus+ member price — when provided, a marker is shown on the slider indicating
   * the commission level at which the customer total matches LuxPlus+ pricing */
  memberPrice?: number
}

const LP_PLUS_MARKER_THRESHOLD = 3
const SLIDER_HALF_STEP = 0.25

function isSliderAtLuxPlusStep(sliderValue: number, luxPlusSliderValue: number | null): boolean {
  return luxPlusSliderValue !== null && Math.abs(sliderValue - luxPlusSliderValue) < SLIDER_HALF_STEP
}

function AgentCommissionPanel({ offerId, offerTotal, retailValue, packageId, optionId, offer, commissionPercentage: commissionPercentageProp, memberPrice }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const customerView = useAppSelector(state => state.agentHub.customerView)
  const hasExternalCommission = commissionPercentageProp !== undefined

  const { commissionPercentage: hookCommissionPercentage, packagesCommission, dateRangesCommission } = useAgentHubCommission(
    hasExternalCommission ? '' : offerId,
    hasExternalCommission ? undefined : offer,
  )

  const dateRangeCommission = optionId ? dateRangesCommission[optionId] : null
  const packageCommission = packageId ? packagesCommission.find(pkg => pkg.packageId === packageId) : null

  const effectiveCommission = useMemo(() => {
    if (hasExternalCommission) {
      return commissionPercentageProp
    }
    const dateRangeExtra = dateRangeCommission?.commissionPercentage ?? 0
    if (packageCommission?.commissionPercentage) {
      return packageCommission.commissionPercentage + dateRangeExtra
    }
    return hookCommissionPercentage + dateRangeExtra
  }, [hasExternalCommission, commissionPercentageProp, dateRangeCommission, hookCommissionPercentage, packageCommission])

  const [sliderValue, setSliderValue] = useState(effectiveCommission)

  const interactionBaselineRef = useRef<number | null>(null)
  const hasTrackedFirstUseRef = useRef(false)

  useEffect(() => {
    interactionBaselineRef.current = null
    setSliderValue(effectiveCommission)
    dispatch(setAgentExtraEarnings(0))
  }, [dispatch, effectiveCommission])

  const trackDirectionEvent = useDebouncedCallback(
    (value: number) => {
      const baseline = interactionBaselineRef.current
      if (baseline === null || value === baseline) return

      trackClientEvent({
        subject: 'commission_slider',
        action: value > baseline ? 'price_up' : 'price_down',
        category: ANALYTICS_CATEGORY,
        type: 'interaction',
      })

      interactionBaselineRef.current = value
    },
    ANALYTICS_DEBOUNCE_MS,
    [],
  )

  const rrpLabel = intl.formatMessage({
    id: 'w2DVWZE_dB-3U33WU3pm',
    defaultMessage: 'RRP',
    description: 'Marker label on the commission slider indicating the recommended retail price point',
  })

  const luxPlusLabel = intl.formatMessage({
    id: '9tNQdhfMpvVINasN_lpd',
    defaultMessage: 'LuxPlus+',
    description: 'Marker label on the commission slider indicating the LuxPlus+ member price point',
  })

  const luxPlusSliderValue = useMemo(() => {
    if (memberPrice && offerTotal > 0) {
      const value = effectiveCommission + (memberPrice / offerTotal - 1) * 100
      const sliderDifference = effectiveCommission - value
      if (value >= 0 && sliderDifference >= LP_PLUS_MARKER_THRESHOLD) {
        return value
      }
    }
    return null
  }, [effectiveCommission, memberPrice, offerTotal])

  const markers = useMemo(() => {
    const result: Array<{ value: number; label: string; kind?: 'default' | 'highlight' }> = []
    if (effectiveCommission > 0) {
      result.push({ value: effectiveCommission, label: rrpLabel })
    }
    if (luxPlusSliderValue !== null) {
      result.push({ value: luxPlusSliderValue, label: luxPlusLabel, kind: 'highlight' })
    }
    return result
  }, [effectiveCommission, luxPlusSliderValue, rrpLabel, luxPlusLabel])

  const { totalEarnings, customerTotal, customerSavings } = useMemo(() => {
    const adjustmentFromRRP = sliderValue - effectiveCommission
    const totalEarnings = offerTotal * (sliderValue / 100)
    const rawCustomerTotal = offerTotal + offerTotal * (adjustmentFromRRP / 100)
    const customerTotal = isSliderAtLuxPlusStep(sliderValue, luxPlusSliderValue) && memberPrice !== undefined
      ? memberPrice
      : Math.round(rawCustomerTotal)
    const customerSavings = retailValue ? Math.max(0, retailValue - customerTotal) : 0
    return { totalEarnings, customerTotal, customerSavings }
  }, [sliderValue, effectiveCommission, offerTotal, retailValue, luxPlusSliderValue, memberPrice])

  const handleSliderChange = useCallback((value: number) => {
    setSliderValue(value)
    const extraEarnings = Math.round(offerTotal * (value - effectiveCommission)) / 100
    dispatch(setAgentExtraEarnings(extraEarnings))

    if (interactionBaselineRef.current === null) {
      interactionBaselineRef.current = effectiveCommission
    }

    if (!hasTrackedFirstUseRef.current) {
      hasTrackedFirstUseRef.current = true
      trackClientEvent({
        subject: 'commission_slider',
        action: 'used',
        category: ANALYTICS_CATEGORY,
        type: 'interaction',
      })
    }

    trackDirectionEvent(value)
  }, [dispatch, effectiveCommission, offerTotal, trackDirectionEvent])

  if (!config.agentHub.isEnabled || !effectiveCommission || customerView) {
    return null
  }

  const tooltipDescription = intl.formatMessage({
    id: 'v0iDPUkv4kj-73Tl4s2g',
    defaultMessage: 'Adjust the slider to increase or decrease your commission. This changes your client\'s price, but your net payout from Agent Hub does not change',
    description: 'Tooltip explaining how the commission adjustment slider works for agents',
  })

  return (
    <FullWidthSheet padding={8} border="neutral-five" borderWidth={1} borderRadius="L">
      <CollapsibleGroup
        label={(
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            <BodyTextBlock
              variant="medium"
              weight="bold"
              startIcon={<Tooltip description={tooltipDescription} portaled><LineInfoCircleIcon size="S" /></Tooltip>}
            >
              {intl.formatMessage({
                id: 'lTauwoXM5YEByuVHUrD3',
                defaultMessage: 'Your commission rate',
                description: 'Section heading for the agent commission adjustment panel',
              })}
            </BodyTextBlock>
            <Label kind="tertiary" variant="highlight">
              {intl.formatMessage({
                id: 'WU-wxV8_pwbuj7VF4G1Y',
                defaultMessage: '{percentage}% applied',
                description: 'Label showing the currently applied commission percentage',
              }, { percentage: effectiveCommission })}
            </Label>
          </Group>
        )}
        noPadding
      >
        <ContentSpacer gap={16}>
          <AgentHubMarkerSlider
            min={0}
            max={MAX_MARKUP_PERCENTAGE}
            step={0.5}
            value={sliderValue}
            markers={markers}
            minLabel={intl.formatMessage({
              id: 'iTvbBbg_NeQSqqzO_Y9q',
              defaultMessage: '{percentage}% Markup',
              description: 'Minimum markup label on the commission slider in the agent hub portal',
            }, { percentage: 0 })}
            maxLabel={intl.formatMessage({
              id: 'JJpwRc0a3iMs5C_vzNxG',
              defaultMessage: '{percentage}% Markup',
              description: 'Maximum markup label on the commission slider in the agent hub portal',
            }, { percentage: MAX_MARKUP_PERCENTAGE })}
            onChange={handleSliderChange}
          />
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'jU6AXam7VIJc5bPW5NI0',
                defaultMessage: 'Your earnings',
                description: 'Label for the agent earnings row in the commission panel',
              })}
            </BodyText>
            <BodyText variant="medium" weight="bold" colour="highlight-secondary">
              <FormatCurrency value={totalEarnings} />
            </BodyText>
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'Z843oXzh6h9JNu4oAPp3',
                defaultMessage: 'Customer total',
                description: 'Label for the customer total price row in the commission panel',
              })}
            </BodyText>
            <BodyText variant="medium" weight="bold" colour="highlight-secondary">
              <FormatCurrency value={customerTotal} />
            </BodyText>
          </Group>
          {customerSavings > 0 && (
            <Sheet background="highlight-primary" borderRadius="S" padding={12}>
              <BodyTextBlock variant="medium" weight="bold" startIcon={checkIcon}>
                {intl.formatMessage({
                  id: 'lYn1kwFbJ-NldBtaMWfX',
                  defaultMessage: 'Customer saves {amount}',
                  description: 'Text showing how much the customer saves, with the currency amount interpolated',
                }, { amount: <FormatCurrency value={customerSavings} /> })}
              </BodyTextBlock>
            </Sheet>
          )}
        </ContentSpacer>
      </CollapsibleGroup>
    </FullWidthSheet>
  )
}

export default AgentCommissionPanel
